import React from "react";
import { useNavigate } from "react-router-dom";

const DeveloperTools = () => {
  const navigate = useNavigate();

  const tools = [
    {
      category: "Core SDK",
      items: [
        { name: "JavaScript SDK", description: "Complete JS library for dApp development", icon: "🟨", docs: "View Docs" },
        { name: "Python SDK", description: "Python integration for backend services", icon: "🐍", docs: "View Docs" },
        { name: "REST API", description: "Full REST API for chain interactions", icon: "🌐", docs: "API Reference" }
      ]
    },
    {
      category: "Smart Contracts",
      items: [
        { name: "Solidity Templates", description: "Pre-built contract templates", icon: "📝", docs: "Get Started" },
        { name: "Hardhat Plugins", description: "Development environment plugins", icon: "⚒️", docs: "Install" },
        { name: "Truffle Suite", description: "Full development suite support", icon: "🍫", docs: "Guide" }
      ]
    },
    {
      category: "Testing & Deployment",
      items: [
        { name: "Testnet Faucet", description: "Get test tokens for development", icon: "🚰", docs: "Get Tokens" },
        { name: "Local Node", description: "Run a local development node", icon: "💻", docs: "Setup Guide" },
        { name: "Deployment Tools", description: "One-click deployment solutions", icon: "🚀", docs: "Deploy" }
      ]
    }
  ];

  const quickStartSteps = [
    {
      step: 1,
      title: "Set Up Environment",
      description: "Install our CLI tools and configure your development environment",
      command: "npm install -g @xecosystem/cli"
    },
    {
      step: 2,
      title: "Initialize Project",
      description: "Create a new dApp project with our starter template",
      command: "xecosystem create my-dapp"
    },
    {
      step: 3,
      title: "Run Local Node",
      description: "Start a local development blockchain for testing",
      command: "xecosystem node start"
    },
    {
      step: 4,
      title: "Deploy Contracts",
      description: "Deploy your smart contracts to the testnet",
      command: "xecosystem deploy --network testnet"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-eco-black to-eco-dark pt-20">
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-eco-green hover:text-eco-blue mb-8 transition-colors"
        >
          ← Back to Ecosystem
        </button>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="w-20 h-20 bg-gradient-to-br from-eco-green to-eco-blue rounded-3xl flex items-center justify-center text-eco-black text-3xl mx-auto mb-6">
            🔧
          </div>
          <h1 className="text-5xl font-black gradient-text mb-4">Developer Tools</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Build next-gen dApps with our comprehensive developer toolkit and documentation. 
            Everything you need to create sustainable Web3 applications.
          </p>
        </div>

        {/* Tools Grid */}
        <div className="space-y-12 mb-16">
          {tools.map((toolCategory, categoryIndex) => (
            <div key={categoryIndex} className="glass-card rounded-3xl p-8 border border-eco-green border-opacity-20">
              <h2 className="text-2xl font-black text-white mb-6">{toolCategory.category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {toolCategory.items.map((tool, toolIndex) => (
                  <div key={toolIndex} className="bg-eco-dark rounded-2xl p-6 border border-gray-700 hover:border-eco-green transition-all duration-300 group">
                    <div className="flex items-start mb-4">
                      <div className="text-2xl mr-4 group-hover:scale-110 transition-transform duration-300">
                        {tool.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-1">{tool.name}</h3>
                        <p className="text-gray-400 text-sm">{tool.description}</p>
                      </div>
                    </div>
                    <button className="w-full py-2 bg-eco-dark border border-eco-green border-opacity-30 rounded-xl text-eco-green font-semibold hover:bg-eco-green hover:bg-opacity-10 transition-all duration-300">
                      {tool.docs}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Quick Start Guide */}
        <div className="glass-card rounded-3xl p-8 mb-16 border border-eco-green border-opacity-20">
          <h2 className="text-3xl font-black text-white text-center mb-8">Quick Start Guide</h2>
          <div className="space-y-6">
            {quickStartSteps.map((step, index) => (
              <div key={index} className="flex flex-col md:flex-row items-start bg-eco-dark rounded-2xl p-6 border border-gray-700">
                <div className="flex items-center mb-4 md:mb-0 md:mr-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-eco-green to-eco-blue rounded-2xl flex items-center justify-center text-eco-black font-bold text-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-300 mb-4">{step.description}</p>
                  <div className="bg-black rounded-xl p-4 font-mono text-sm text-eco-green">
                    {step.command}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Resources & Community */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="glass-card p-8 rounded-3xl border border-eco-green border-opacity-20">
            <h3 className="text-2xl font-bold text-white mb-4">Learning Resources</h3>
            <ul className="space-y-3">
              <li className="flex items-center text-eco-blue hover:text-eco-green transition-colors cursor-pointer">
                <span className="w-2 h-2 bg-eco-blue rounded-full mr-3"></span>
                Comprehensive Documentation
              </li>
              <li className="flex items-center text-eco-blue hover:text-eco-green transition-colors cursor-pointer">
                <span className="w-2 h-2 bg-eco-blue rounded-full mr-3"></span>
                Video Tutorial Series
              </li>
              <li className="flex items-center text-eco-blue hover:text-eco-green transition-colors cursor-pointer">
                <span className="w-2 h-2 bg-eco-blue rounded-full mr-3"></span>
                Interactive Code Examples
              </li>
              <li className="flex items-center text-eco-blue hover:text-eco-green transition-colors cursor-pointer">
                <span className="w-2 h-2 bg-eco-blue rounded-full mr-3"></span>
                Best Practices Guide
              </li>
            </ul>
          </div>
          <div className="glass-card p-8 rounded-3xl border border-eco-green border-opacity-20">
            <h3 className="text-2xl font-bold text-white mb-4">Developer Community</h3>
            <ul className="space-y-3">
              <li className="flex items-center text-eco-green hover:text-eco-blue transition-colors cursor-pointer">
                <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                Discord Developer Channel
              </li>
              <li className="flex items-center text-eco-green hover:text-eco-blue transition-colors cursor-pointer">
                <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                GitHub Discussions
              </li>
              <li className="flex items-center text-eco-green hover:text-eco-blue transition-colors cursor-pointer">
                <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                Stack Overflow Tags
              </li>
              <li className="flex items-center text-eco-green hover:text-eco-blue transition-colors cursor-pointer">
                <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                Weekly Office Hours
              </li>
            </ul>
          </div>
        </div>

        {/* CTA Section */}
        <div className="glass-card rounded-3xl p-8 text-center border border-eco-green border-opacity-20">
          <h3 className="text-2xl font-black text-white mb-4">Ready to Start Building?</h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Join thousands of developers building the future of sustainable Web3 applications on X EcoChain.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-eco px-8 py-3 font-bold">
              View Full Documentation
            </button>
            <button className="btn-secondary px-8 py-3 font-bold">
              Join Discord
            </button>
            <button className="btn-secondary px-8 py-3 font-bold">
              Explore GitHub
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeveloperTools;